/**
 * At the time of build, we append a timestamp to the version.json file.
 * This is so that we can bust the cache of the file and force a new version to be fetched.
 * This is useful when we are developing locally and want to test the latest changes.
 */

import { useEffect, useRef } from 'react'

import getConfig from 'next/config'
import { useRouter } from 'next/router'

import logger from '~/utils/logger'

const config = getConfig()
const { buildTimestamp: configBuildTimestamp } = config.publicRuntimeConfig

type CacheBustingProps = { children: React.ReactNode }
export const CacheBusting = ({ children }: CacheBustingProps) => {
  const router = useRouter()
  const { pathname } = router
  const fetchingRef = useRef(false)

  useEffect(() => {
    // fetch the version.json file at the time of route change,
    // compare the buildTimestamp from next.config.js with the buildTimestamp from server version.json
    // if the version from the server is more recent, then we need to bust the cache
    async function checkVersion() {
      fetchingRef.current = true
      try {
        const res = await fetch(`/version.json`) // eTag should be able to handle caching of version.json
        const data = await res.json()
        const { buildTimestamp: serverBuildTimestamp } = data
        if (serverBuildTimestamp > configBuildTimestamp) {
          // programatically reload the page to force a new version to be fetched
          window.location.reload()
        }
      } catch (error) {
        logger.error('Error fetching version.json', error)
      } finally {
        fetchingRef.current = false
      }
    }

    // dont fetch version.json if it is already being fetched
    if (!fetchingRef.current) {
      checkVersion()
    }
  }, [pathname])

  return <>{children}</>
}
